import { Amplify } from "aws-amplify";
import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import * as Sentry from "@sentry/react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  ScrollRestoration,
  Navigate,
} from "react-router-dom";
import { MantineProvider } from "@mantine/core";
import { Userpilot } from "userpilot";
import { usePostHog } from "posthog-js/react";
import { AutoLogout } from "shared/src/components/AutoLogout";
import Settings from "@/pages/Settings";
import ClaimableAuthenticator from "@/components/shared/ClaimableAuthenticator";
import IdentifyUser from "@/components/IdentifyUser";
import PageViewTracker from "@/components/PageViewTracker";
import mantineTheme from "@/mantineTheme";
import { FormProvider } from "@/contexts/FormContext";
import { NavigationBlocker } from "@/components/NavigationBlocker";
import { DevEnvironmentBanner } from "@/components/DevEnvironmentBanner";
import graphQLClient from "@/apolloClient";

import "./App.scss";
import "@aws-amplify/ui-react/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import HomePage from "@/pages/HomePage/HomePage";
import ReferPatient from "@/pages/ReferPatient/ReferPatient";
import { UserProviderSetupWrapper } from "./components/UserProviderSetupWrapper/UserProviderSetupWrapper";
import ReferPatientDocuments from "@/pages/ReferPatientDocuments";
import ReferPatientInfo from "@/pages/ReferPatientInfo/ReferPatientInfo";
import { InitialReferralPage } from "@/pages/InitialReferralPage/InitialReferralPage";
import { Patient } from "@/pages/Patient/Patient";
import { Insurance } from "@/pages/Insurance/Insurance";
import { Documents } from "@/pages/Documents/Documents";
import { PrescriberInformation } from "@/pages/PrescriberInformation/PrescriberInformation";
import { MedicalInfoTabRoute } from "@/pages/MedicalInfo/MedicalInfoTabRoute";
import { PatientSurvey } from "@/pages/PatientSurvey/PatientSurvey";
import { SubmissionDetails } from "@/pages/SubmissionDetails/SubmissionDetails";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
      allowGuestAccess: true,
    },
  },
});

Userpilot.initialize("NX-32bc6362");

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

function AutoLogoutWrapper() {
  const { signOut } = useAuthenticator();
  const posthog = usePostHog();

  const handleLogout = async () => {
    await graphQLClient.clearStore();
    signOut();
  };

  const handleInactivityLogout = () => {
    posthog.capture("auth_inactivity_logout");
  };

  return (
    <AutoLogout
      onLogout={handleLogout}
      onInactivityLogout={handleInactivityLogout}
    />
  );
}

function App() {
  const router = sentryCreateBrowserRouter([
    {
      element: (
        <ClaimableAuthenticator>
          <DevEnvironmentBanner />
          <IdentifyUser />
          <AutoLogoutWrapper />
          <UserProviderSetupWrapper>
            <ScrollRestoration />
            <PageViewTracker />
            <NavigationBlocker />
            <Outlet />
          </UserProviderSetupWrapper>
        </ClaimableAuthenticator>
      ),
      children: [
        {
          path: "/",
          element: <HomePage />,
        },
        { path: "/refer-patient", element: <ReferPatient /> },
        { path: "/refer", element: <InitialReferralPage /> },
        {
          path: "/refer-patient-documents",
          element: <ReferPatientDocuments />,
        },
        {
          path: "/refer/:appealId/patient-info",
          element: <ReferPatientInfo />,
        },
        {
          path: "/refer/:appealId/enhance",
          element: <Navigate to="/" replace />,
        },
        {
          path: "/appeals/:appealId/strengthen/patient",
          element: <Patient />,
        },
        {
          path: "/appeals/:appealId/strengthen/insurance",
          element: <Insurance />,
        },
        {
          path: "/appeals/:appealId/strengthen/documents",
          element: <Documents />,
        },
        {
          path: "/appeals/:appealId/strengthen/prescriber-information",
          element: <PrescriberInformation />,
        },
        {
          path: "/appeals/:appealId/strengthen/medical-info/:tabId",
          element: <MedicalInfoTabRoute />,
        },
        {
          path: "/appeals/:appealId/strengthen/patient-survey",
          element: <PatientSurvey />,
        },
        {
          path: "/appeals/:appealId/strengthen/submission-details",
          element: <SubmissionDetails />,
        },
        { path: "/settings", element: <Settings /> },
      ],
    },
  ]);

  return (
    <MantineProvider theme={mantineTheme}>
      <Authenticator.Provider>
        <FormProvider>
          <RouterProvider router={router} />
        </FormProvider>
      </Authenticator.Provider>
    </MantineProvider>
  );
}

export default App;
